import { useEffect, useMemo, useRef, useState } from "react";
import {
  Backdrop,
  Box,
  Button,
  Fade,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import swal from "sweetalert2";
import { GetDomiciliosByCompany } from "../../../../../hooks/getDomicilios";
import { TiposDomicilioEnum } from "../../../../../@enums/tipo_domicilio.enum";
import useApi from "../../../../../hooks/useApiRequest";
import { useLoading } from "../../../../../components/Loading/useLoading";

const style = {
  position: "absolute",
  top: "45%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 520,
  maxWidth: "95vw",
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 3,
};

const etiquetaDomicilio = (dom) => {
  const tipo =
    dom?.domi_tipo === TiposDomicilioEnum.COMERCIAL
      ? "Comercial"
      : dom?.domi_tipo === TiposDomicilioEnum.ENVIO
        ? "Envio"
        : dom?.domi_tipo === TiposDomicilioEnum.FISCAL
          ? "Fiscal"
          : "Postal";
  return `${tipo} - ${dom?.domi_colonia} ${dom?.domi_cp} ${dom?.domi_municipio}, ${dom?.domi_estado}, ${dom?.domi_pais}`;
};

const mensajeError = async (error) => {
  const data = error?.response?.data;
  if (data instanceof Blob) {
    try {
      const json = JSON.parse(await data.text());
      if (typeof json?.message === "string") return json.message;
      if (typeof json?.mensaje === "string") return json.mensaje;
    } catch {
      return "No se pudo generar el packing list";
    }
  }
  if (typeof data?.message === "string") return data.message;
  if (typeof data?.mensaje === "string") return data.mensaje;
  return "No se pudo generar el packing list";
};

export const ModalPackingList = ({ open, setOpen, almacen, cargados }) => {
  const api = useApi();
  const { setAppLoading } = useLoading();
  const { getDomiciliosByCompanyMutation } = GetDomiciliosByCompany();
  const [empresa, setEmpresa] = useState("");
  const [domicilio, setDomicilio] = useState("");
  const [domicilios, setDomicilios] = useState([]);
  const [fechaInicio, setFechaInicio] = useState("");
  const [fechaFin, setFechaFin] = useState("");
  const empresaActual = useRef("");

  const empresas = useMemo(() => {
    const unicas = new Map();
    (cargados || []).forEach((row) => {
      if (row?.emp_id != null && !unicas.has(row.emp_id)) {
        unicas.set(row.emp_id, row.cliente || `Empresa ${row.emp_id}`);
      }
    });
    return Array.from(unicas, ([id, label]) => ({ id, label }));
  }, [cargados]);

  useEffect(() => {
    if (!open) return;
    setEmpresa("");
    setDomicilio("");
    setDomicilios([]);
    setFechaInicio("");
    setFechaFin("");
  }, [open]);

  useEffect(() => {
    empresaActual.current = empresa;
    setDomicilio("");
    setDomicilios([]);
    if (!empresa) return;
    const solicitada = empresa;
    getDomiciliosByCompanyMutation(solicitada, {
      onSuccess: (data) => {
        if (empresaActual.current !== solicitada) return;
        setDomicilios(
          (data || []).map((dom) => ({
            id: dom?.domi_id,
            label: etiquetaDomicilio(dom),
          })),
        );
      },
      onError: () => {
        if (empresaActual.current !== solicitada) return;
        setDomicilios([]);
      },
    });
  }, [empresa]);

  const rangoInvalido =
    Boolean(fechaInicio) && Boolean(fechaFin) && fechaFin < fechaInicio;
  const puedeGenerar =
    Boolean(empresa) &&
    Boolean(domicilio) &&
    Boolean(fechaInicio) &&
    Boolean(fechaFin) &&
    !rangoInvalido;

  const cerrar = () => setOpen(false);

  const generar = async () => {
    if (!puedeGenerar) return;
    setAppLoading(true);
    try {
      const response = await api.post(
        "/administracion-inventario/packing-list/cargados",
        {
          alma_id: almacen,
          emp_id: empresa,
          domi_id: domicilio,
          fecha_inicio: fechaInicio,
          fecha_fin: fechaFin,
        },
        { dataType: "json", responseType: "blob" },
      );
      if (!response || response.isAxiosError || response.response) {
        const mensaje = await mensajeError(response);
        swal.fire({
          position: "center",
          icon: "warning",
          title: mensaje,
          showConfirmButton: true,
          customClass: { container: "cont_alt" },
        });
        return;
      }
      const disposition = response.headers?.["content-disposition"] ?? "";
      const match = disposition.match(/filename="?([^"]+)"?/);
      const fileName = match?.[1] ?? "packing-list.pdf";
      const blob =
        response.data instanceof Blob
          ? response.data
          : new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      cerrar();
    } finally {
      setAppLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={cerrar}
      closeAfterTransition
      slots={{ backdrop: Backdrop }}
      slotProps={{ backdrop: { timeout: 400 } }}
    >
      <Fade in={open}>
        <Box sx={style}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Packing list
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth size="small" disabled={empresas.length === 0}>
                <InputLabel id="packing_empresa">Empresa</InputLabel>
                <Select
                  labelId="packing_empresa"
                  label="Empresa"
                  value={empresa}
                  onChange={(event) => setEmpresa(event.target.value)}
                >
                  {empresas.map((item) => (
                    <MenuItem key={item.id} value={item.id}>
                      {item.label}
                    </MenuItem>
                  ))}
                </Select>
                {empresas.length === 0 && (
                  <FormHelperText>
                    No hay empresas en las partes cargadas
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small" disabled={!empresa}>
                <InputLabel id="packing_domicilio">Domicilio</InputLabel>
                <Select
                  labelId="packing_domicilio"
                  label="Domicilio"
                  value={domicilio}
                  onChange={(event) => setDomicilio(event.target.value)}
                >
                  {domicilios.map((item) => (
                    <MenuItem key={item.id} value={item.id}>
                      {item.label}
                    </MenuItem>
                  ))}
                </Select>
                {empresa && domicilios.length === 0 && (
                  <FormHelperText>Esta empresa no tiene domicilios</FormHelperText>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField
                type="date"
                size="small"
                fullWidth
                label="Fecha inicial"
                InputLabelProps={{ shrink: true }}
                value={fechaInicio}
                onChange={(event) => setFechaInicio(event.target.value)}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                type="date"
                size="small"
                fullWidth
                label="Fecha final"
                InputLabelProps={{ shrink: true }}
                value={fechaFin}
                onChange={(event) => setFechaFin(event.target.value)}
                error={rangoInvalido}
                helperText={
                  rangoInvalido ? "La fecha final no puede ser anterior" : " "
                }
              />
            </Grid>
            <Grid item xs={6}>
              <Button
                variant="outlined"
                className="btn_system"
                fullWidth
                onClick={cerrar}
              >
                Cancelar
              </Button>
            </Grid>
            <Grid item xs={6}>
              <Button
                variant="contained"
                className="btn_system"
                fullWidth
                disableElevation
                disabled={!puedeGenerar}
                onClick={generar}
              >
                Generar
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Fade>
    </Modal>
  );
};
